import { ObjectSchema, string } from 'yup'

import { QA_MAILSOUR_EMAIL_DOMAIN } from 'lib/constants'

export type LoginFormValues = {
  email: string
  password: string
}

export type RegisterFormValues = {
  first_name: string
  last_name: string
  email: string
  password: string
  terms: boolean
  recaptcha: string
}

type FormValues = LoginFormValues | RegisterFormValues

export const containsUppercaseRegExp = new RegExp(/[A-Z]/)
export const containsLowercaseRegExp = new RegExp(/[a-z]/)
export const containsNumericRegExp = new RegExp(/[0-9]/)
export const containsSpecialRegExp = new RegExp(/[^\w\s]/)
export const isValidEmailRegExp = new RegExp(
  // From: https://stackoverflow.com/questions/201323/how-can-i-validate-an-email-address-using-a-regular-expression
  /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/
)

export const passwordValidator = string()
  .required('Enter a Password')
  .min(8, 'Must be more than 8 characters')
  .max(120, 'Must be under 120 characters')

export const registrationFormPasswordValidator = string()
  .required('Enter a Password')
  .min(8, 'Must be at least 8 characters in length')
  .max(120, 'Must be not more than 120 characters')
  .test(
    'contains uppercase and lowercase characters',
    'Must contain upper and lower case characters',
    value => containsUppercaseRegExp.test(value) && containsLowercaseRegExp.test(value)
  )
  .test('contains numerical characters', 'Must contain at least one numerical character', value =>
    containsNumericRegExp.test(value)
  )
  .test('contains special characters', 'Must contain at least one special character', value =>
    containsSpecialRegExp.test(value)
  )

export const emailValidator = string().email('Enter a valid email').required('Enter your email address')

export const emailValidatorNoAlias = string()
  .test('no-email-alias', 'The use of the "+" symbol for email aliases is not supported', value => {
    const whitelistedEmails = ['@consensys.net', QA_MAILSOUR_EMAIL_DOMAIN] // Whitelist for emails that require alias for development and testing

    // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
    if (whitelistedEmails.some(domain => value.includes(domain))) {
      return true
    }

    // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
    const hasPlusSymbolBeforeAt = value.includes('+') && value.indexOf('+') < value.indexOf('@')
    if (hasPlusSymbolBeforeAt) {
      return false
    }

    return true
  })
  .email('Enter a valid email')
  .required('Enter your email address')

interface GetValidationErrorsProps {
  values: FormValues
  validationSchema: ObjectSchema<FormValues>
}

// Returns errors from yup in a format that can be easily rendered by Final Form.
export async function getValidationErrors({ validationSchema, values }: GetValidationErrorsProps) {
  const errors = {}
  const isValid = await validationSchema.isValid(values)

  // Form is valid, return no errors.
  if (isValid) return errors

  // Map validation errors to values object.
  return await validationSchema.validate(values, { abortEarly: false }).catch(err =>
    err.inner.reduce((acc, error) => {
      return {
        ...acc,
        [error.path]: error.message
      }
    }, {})
  )
}

export const onboardingRoleValidator = {
  role: string().required('Select Role'),
  role_other: string().when('role', {
    is: role => role === 'other',
    otherwise: schema => schema,
    then: schema =>
      schema.required('Please describe your role').min(3, 'Minimum 3 characters').max(100, 'Maximum 100 characters')
  })
}

export const onboardingUsageValidator = {
  usage: string().required('Select Usage')
}

export const onboardingCategoryValidator = {
  category: string().required('Select Category'),
  category_other: string().when('category', {
    is: category => category === 'other',
    otherwise: schema => schema,
    then: schema =>
      schema.required('Please describe marketplace').min(5, 'Minimum 5 characters').max(200, 'Maximum 200 characters')
  })
}
